/**
 * Composable for common formatting utilities
 * Provides reusable functions for formatting amounts, dates, and file sizes
 */

/**
 * Format amount to locale string with 2 decimal places
 * Handles various currency symbols and formats
 * @param {number|string} amount - Amount to format
 * @returns {string} Formatted amount
 */
export const formatAmount = (amount) => {
  if (!amount) return '0.00';
  
  // Remove currency symbols and clean the string
  const cleanAmount = amount.toString()
    .replace(/[$€£¥₹₽BGN]/g, '')
    .replace(/\s/g, '')
    .replace(',', '.');
  
  const numAmount = parseFloat(cleanAmount);
  if (isNaN(numAmount)) return '0.00';
  
  return numAmount.toLocaleString('en-US', { 
    minimumFractionDigits: 2, 
    maximumFractionDigits: 2 
  });
};

/**
 * Format date to absolute date string
 * @param {string} dateString - ISO date string
 * @returns {string} Formatted date string
 */
export const formatDate = (dateString) => {
  if (!dateString) return 'Unknown';

  const date = new Date(dateString);

  // Always show absolute date
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  });
};

/**
 * Format file size in bytes to human-readable format (B, KB, MB, GB)
 * @param {number} bytes - File size in bytes
 * @returns {string} Formatted file size
 */
export const formatFileSize = (bytes) => {
  if (!bytes) return '0 B';
  
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  
  return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
};

/**
 * Format currency with symbol
 * @param {number|string} amount - Amount to format
 * @param {string} currency - Currency symbol (default: 'BGN')
 * @returns {string} Formatted currency string
 */
export const formatCurrency = (amount, currency = 'BGN') => {
  const formatted = formatAmount(amount);
  return `${formatted} ${currency}`;
};

/**
 * Composable hook that returns all formatter functions
 * @returns {Object} Object containing all formatter functions
 */
export function useFormatters() {
  return {
    formatAmount,
    formatDate,
    formatFileSize,
    formatCurrency
  };
}
